import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import RestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import ConfirmationDialog from '@tracker/shared/src/ConfirmationDialog';
import {useResetPreferences, useUpdatePreferences} from '@tracker/shared/src/Preferences/PreferencesProvider';
import React, {useCallback, useState} from 'react';
import {DragDropContext, DraggableLocation, Droppable, DropResult} from 'react-beautiful-dnd';
import {BookmarkLinkGroupWithId, BookmarkLinkWithId} from './Home';
import LinkGroupCard from './LinkGroupCard';
import LinkGroupFormDialog from './LinkGroupFormDialog';

const useStyles = makeStyles(theme => ({
    spacer: {
        flexGrow: 1,
    },
    card: {
        marginBottom: theme.spacing(2),
    },
}));

const reorder = <T extends unknown>(links : T[], startIndex : number, endIndex : number) : T[] => {
    const result = [...links];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const move = (
    sourceLinks : BookmarkLinkWithId[],
    destinationLinks : BookmarkLinkWithId[],
    droppableSource : DraggableLocation,
    droppableDestination : DraggableLocation
) => {
    const newSourceLinks = [...sourceLinks];
    const newDestinationLinks = [...destinationLinks];
    const [removed] = newSourceLinks.splice(droppableSource.index, 1);
    newDestinationLinks.splice(droppableDestination.index, 0, removed);

    return [newSourceLinks, newDestinationLinks];
};

type Props = {
    linkGroups : BookmarkLinkGroupWithId[];
};

const EditView : React.FC<Props> = ({linkGroups} : Props) => {
    const classes = useStyles();
    const updatePreferences = useUpdatePreferences();
    const resetPreferences = useResetPreferences();
    const [createLinkGroupDialogOpen, setCreateLinkGroupDialogOpen] = useState(false);
    const [confirmRestoreDialogOpen, setConfirmRestoreDialogOpen] = useState(false);

    const handleRestore = () => {
        return resetPreferences({dashboard: ['linkGroups']});
    };

    const handleCreateLinkGroup = useCallback(async (linkGroup : BookmarkLinkGroupWithId) => {
        return updatePreferences({
            dashboard: {
                linkGroups: [...linkGroups, linkGroup],
            },
        });
    }, [updatePreferences, linkGroups]);

    const handleGroupUpdate = useCallback(async (linkGroup : BookmarkLinkGroupWithId) => {
        const newLinkGroups = linkGroups.map(currentLinkGroup => currentLinkGroup.id === linkGroup.id
            ? linkGroup
            : currentLinkGroup
        );

        return updatePreferences({
            dashboard: {linkGroups: newLinkGroups},
        });
    }, [updatePreferences, linkGroups]);

    const handleGroupDelete = useCallback(async (linkGroup : BookmarkLinkGroupWithId) => {
        const newLinkGroups = linkGroups.filter(currentLinkGroup => currentLinkGroup.id !== linkGroup.id);

        return updatePreferences({
            dashboard: {linkGroups: newLinkGroups},
        });
    }, [updatePreferences, linkGroups]);

    const handleDragEnd = useCallback(async ({source, destination, type} : DropResult) => {
        if (!destination) {
            return;
        }

        if (type === 'groups') {
            const newLinkGroups = reorder(linkGroups, source.index, destination.index);
            return updatePreferences({
                dashboard: {linkGroups: newLinkGroups},
            });
        }

        const sourceLinkGroup = linkGroups.find(linkGroup => `group-${linkGroup.id}` === source.droppableId);

        if (!sourceLinkGroup) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            const links = reorder(sourceLinkGroup.links, source.index, destination.index);

            return updatePreferences({
                dashboard: {
                    linkGroups: linkGroups.map(linkGroup => linkGroup.id !== sourceLinkGroup.id
                        ? linkGroup
                        : {...sourceLinkGroup, links}
                    ),
                },
            });
        }

        const destinationLinkGroup = linkGroups.find(linkGroup => `group-${linkGroup.id}` === destination.droppableId);

        if (!destinationLinkGroup) {
            return;
        }

        const [newSourceLinks, newDestinationLinks] = move(
            sourceLinkGroup.links,
            destinationLinkGroup.links,
            source,
            destination
        );

        return updatePreferences({
            dashboard: {
                linkGroups: linkGroups.map(linkGroup => {
                    if (linkGroup.id === sourceLinkGroup.id) {
                        return {...sourceLinkGroup, links: newSourceLinks};
                    }

                    if (linkGroup.id === destinationLinkGroup.id) {
                        return {...destinationLinkGroup, links: newDestinationLinks};
                    }

                    return linkGroup;
                }),
            },
        });
    }, [linkGroups, updatePreferences]);

    return (
        <>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="groups" type="groups">
                    {(provided, snapshot) => (
                        <div ref={provided.innerRef}>
                            {linkGroups.map((linkGroup, index) => (
                                <LinkGroupCard
                                    key={linkGroup.id}
                                    index={index}
                                    linkGroup={linkGroup}
                                    className={classes.card}
                                    onUpdate={handleGroupUpdate}
                                    onDelete={handleGroupDelete}
                                />
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <Paper>
                <Toolbar>
                    <div className={classes.spacer}/>
                    <Tooltip title="Restore default links">
                        <IconButton onClick={() => setConfirmRestoreDialogOpen(true)} color="inherit">
                            <RestoreIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Add link group">
                        <IconButton onClick={() => setCreateLinkGroupDialogOpen(true)} color="inherit" edge="end">
                            <AddIcon/>
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </Paper>

            <LinkGroupFormDialog
                open={createLinkGroupDialogOpen}
                onSubmit={handleCreateLinkGroup}
                onClose={() => setCreateLinkGroupDialogOpen(false)}
            />

            <ConfirmationDialog
                open={confirmRestoreDialogOpen}
                title="Restore original links"
                message="You are about to restore the default links and undo all modifications."
                onClose={() => setConfirmRestoreDialogOpen(false)}
                onConfirm={handleRestore}
            />
        </>
    );
};

export default EditView;
